import React, { useEffect, useMemo, useState } from "react";
import { TFunction } from "i18next";
import CommittedValidate from "../../components/CommitedValidate";
import WaitingValidate from "../../components/WaitingValidate";
import GenderIcon from "../../components/GenderIcon";
import { calculateAge } from "../../utils/dateUtils";
import { Classroom, Child, FeedbackState, GenderKey } from "./types";
import { FaEnvelope, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";

type ChildSortKey = "name" | "age" | "gender";
type SortDirection = "ascending" | "descending";

const childName = (child: Child) =>
  `${child.firstName || ""} ${child.lastName || ""}`.trim();

const childSortValue = (child: Child, key: ChildSortKey): string | number | null => {
  if (key === "name") return childName(child).toLocaleLowerCase();
  if (key === "gender") return String(child.gender || "").toLocaleLowerCase();
  return child.dateBirth ? calculateAge(child.dateBirth) : null;
};

export const sortChildren = (
  children: Child[],
  key: ChildSortKey,
  direction: SortDirection,
) => {
  const multiplier = direction === "ascending" ? 1 : -1;

  return [...children].sort((left, right) => {
    const leftValue = childSortValue(left, key);
    const rightValue = childSortValue(right, key);

    if (leftValue === null || leftValue === "") return rightValue === null || rightValue === "" ? 0 : 1;
    if (rightValue === null || rightValue === "") return -1;

    const comparison =
      typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, {
            sensitivity: "base",
          });

    if (comparison !== 0) return comparison * multiplier;
    return childName(left).localeCompare(childName(right), undefined, {
      sensitivity: "base",
    });
  });
};

export const selectableChildIds = (children: Child[]) =>
  children.filter((child) => !child.current).map((child) => child.id);

interface Distribution {
  counts: Record<string, number>;
  total: number;
}

interface AgeColors {
  young: string;
  middle: string;
  old: string;
}

interface ChildrenSectionProps {
  childrenList: Child[];
  classroom: Classroom;
  canManage: boolean;
  canViewChild: boolean;
  canViewParentEmails: boolean;
  onViewChild: (id: string) => void;
  onMakeCurrent: (id: string) => void;
  onMakeCurrentSelection: (ids: string[]) => Promise<void>;
  onUnassign: (id: string) => void;
  emailCopyFeedback: FeedbackState | null;
  tripActionFeedback: FeedbackState | null;
  parentEmailLoading: boolean;
  parentEmailCount: number;
  onCopyParentEmails: () => void;
  kindergartenAgeDistribution: Distribution | null;
  genderStats: Distribution | null;
  ageColors: AgeColors;
  outingOverviewCard?: React.ReactNode;
  outingActions?: React.ReactNode;
  t: TFunction;
}

const genderColors: Record<GenderKey, string> = {
  male: "bg-blue-500",
  female: "bg-pink-400",
  other: "bg-gray-500",
};

const ChildrenSection: React.FC<ChildrenSectionProps> = ({
  childrenList,
  classroom,
  canManage,
  canViewChild,
  canViewParentEmails,
  onViewChild,
  onMakeCurrent,
  onMakeCurrentSelection,
  onUnassign,
  emailCopyFeedback,
  tripActionFeedback,
  parentEmailLoading,
  parentEmailCount,
  onCopyParentEmails,
  kindergartenAgeDistribution,
  genderStats,
  ageColors,
  outingOverviewCard,
  outingActions,
  t,
}) => {
  const [sortKey, setSortKey] = useState<ChildSortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("ascending");
  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(() => new Set());
  const [selectionLoading, setSelectionLoading] = useState(false);
  const sortedChildren = useMemo(
    () => sortKey ? sortChildren(childrenList, sortKey, sortDirection) : childrenList,
    [childrenList, sortDirection, sortKey],
  );

  const changeSort = (key: ChildSortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => current === "ascending" ? "descending" : "ascending");
      return;
    }
    setSortKey(key);
    setSortDirection("ascending");
  };

  const sortIcon = (key: ChildSortKey) => {
    if (sortKey !== key) return <FaSort aria-hidden="true" className="text-indigo-200" />;
    return sortDirection === "ascending"
      ? <FaSortUp aria-hidden="true" />
      : <FaSortDown aria-hidden="true" />;
  };

  const ariaSort = (key: ChildSortKey) => sortKey === key ? sortDirection : "none";
  const selectableIds = useMemo(
    () => selectableChildIds(sortedChildren),
    [sortedChildren],
  );
  const allSelectableSelected = selectableIds.length > 0 &&
    selectableIds.every((childId) => selectedChildIds.has(childId));

  useEffect(() => {
    const selectable = new Set(selectableIds);
    setSelectedChildIds((current) => {
      const next = new Set(Array.from(current).filter((childId) => selectable.has(childId)));
      if (next.size === current.size && Array.from(next).every((childId) => current.has(childId))) {
        return current;
      }
      return next;
    });
  }, [selectableIds]);

  const toggleChildSelection = (childId: string) => {
    setSelectedChildIds((current) => {
      const next = new Set(current);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  };

  const makeSelectionCurrent = async () => {
    const ids = Array.from(selectedChildIds);
    if (ids.length === 0) return;
    setSelectionLoading(true);
    try {
      await onMakeCurrentSelection(ids);
      setSelectedChildIds(new Set());
    } catch {
      // The parent reports the translated mutation error; preserve the selection for retry.
    } finally {
      setSelectionLoading(false);
    }
  };

  return (
  <section className="mt-6 rounded-2xl border p-5 shadow-sm border-indigo-100/70 bg-transparent dark:border-indigo-500/40">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
        {t("classrooms.details.assigned_children")}
      </h2>
      <div className="flex flex-wrap items-center gap-3">
        {canManage && selectedChildIds.size > 0 && (
          <button
            type="button"
            onClick={makeSelectionCurrent}
            disabled={selectionLoading}
            className="inline-flex items-center rounded-full bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60"
          >
            {selectionLoading
              ? t("classrooms.details.setting_current_selection", { defaultValue: "Updating selection..." })
              : t("classrooms.details.set_selection_current", {
                  count: selectedChildIds.size,
                  defaultValue: "Make selection current ({{count}})",
                })}
          </button>
        )}
        {canViewParentEmails && (
          <button
            type="button"
            onClick={onCopyParentEmails}
            disabled={parentEmailLoading}
            className={`lumen-glow-sheen inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition ${
              parentEmailLoading
                ? "cursor-wait border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-500"
                : "border-emerald-200/70 bg-emerald-50/80 text-emerald-700 hover:text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-900/40 dark:text-emerald-200"
            }`}
          >
            <FaEnvelope aria-hidden="true" />
            <span>
              {parentEmailLoading
                ? t("classrooms.details.parent_emails_loading", {
                    defaultValue: "Loading email addresses...",
                  })
                : t("classrooms.details.copy_parent_emails", {
                    count: parentEmailCount,
                    defaultValue: "Copy parent emails",
                  })}
              {!parentEmailLoading && parentEmailCount > 0 ? ` (${parentEmailCount})` : ""}
            </span>
          </button>
        )}
        {outingActions}
        <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-gray-900/40 dark:text-indigo-200">
          {childrenList.length} / {classroom.maxChildren ?? "—"}
        </span>
      </div>
    </div>

    {tripActionFeedback && (
      <p
        className={`mt-2 text-xs ${
          tripActionFeedback.type === "success"
            ? "text-emerald-600 dark:text-emerald-300"
            : "text-amber-600 dark:text-amber-300"
        }`}
      >
        {tripActionFeedback.message}
      </p>
    )}

    {emailCopyFeedback && (
      <p
        className={`mt-2 text-xs ${
          emailCopyFeedback.type === "success"
            ? "text-emerald-600 dark:text-emerald-300"
            : "text-amber-600 dark:text-amber-300"
        }`}
      >
        {emailCopyFeedback.message}
      </p>
    )}

    {(kindergartenAgeDistribution || genderStats || outingOverviewCard) && (
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
        {outingOverviewCard && (
          <div className="relative rounded-2xl border border-indigo-100/70 bg-white/90 p-4 shadow-sm dark:border-indigo-800/40 dark:bg-gray-900/60">
            {outingOverviewCard}
          </div>
        )}

        {(kindergartenAgeDistribution || genderStats) && (
          <div className="flex flex-col gap-3">
            {kindergartenAgeDistribution && (
              <div className="rounded-2xl border border-white/70 bg-white/90 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                    {t("classrooms.kindergarten_age_distribution")}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{kindergartenAgeDistribution.total}</span>
                </div>
                {["young", "middle", "old"].map((key) => {
                  const count = kindergartenAgeDistribution.counts[key] || 0;
                  const percent = ((count / kindergartenAgeDistribution.total) * 100).toFixed(0);
                  return (
                    <div key={key} className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{t(`classrooms.kindergarten_ages.${key}`)}</span>
                        <span>
                          {count} ({percent}%)
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-full ${ageColors[key as keyof AgeColors]}`}
                          style={{ width: `${percent}%`, minWidth: count > 0 ? "4px" : "0" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {genderStats && (
              <div className="rounded-2xl border border-white/70 bg-white/90 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                    {t("classrooms.kindergarten_gender_distribution")}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{genderStats.total}</span>
                </div>
                {(Object.keys(genderColors) as GenderKey[]).map((key) => {
                  const count = genderStats.counts[key] || 0;
                  const percent = ((count / genderStats.total) * 100).toFixed(0);
                  return (
                    <div key={key} className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{t(`classrooms.gender.${key}`)}</span>
                        <span>
                          {count} ({percent}%)
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-sky-100/70 dark:bg-sky-950/30">
                        <div
                          className={`h-full rounded-full ${genderColors[key]}`}
                          style={{ width: `${percent}%`, minWidth: count > 0 ? "4px" : "0" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    )}

    {childrenList.length > 0 ? (
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full table-auto border border-gray-200 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100">
          <thead className="indigo-table-head">
            <tr>
              {canManage && (
                <th className="w-12 px-4 py-3 text-center align-middle">
                  <input
                    type="checkbox"
                    checked={allSelectableSelected}
                    onChange={() => setSelectedChildIds(
                      allSelectableSelected ? new Set() : new Set(selectableIds),
                    )}
                    disabled={selectableIds.length === 0 || selectionLoading}
                    aria-label={t("classrooms.details.select_all_children", { defaultValue: "Select all children that are not current" })}
                    className="h-4 w-4 rounded border-indigo-200 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                  />
                </th>
              )}
              <th aria-sort={ariaSort("name")} className="px-4 py-3 text-left align-middle font-medium">
                <button type="button" onClick={() => changeSort("name")} className="inline-flex items-center gap-2">
                  {t("children.table.child_name")}
                  {sortIcon("name")}
                </button>
              </th>
              <th aria-sort={ariaSort("age")} className="px-4 py-3 text-left align-middle font-medium">
                <button type="button" onClick={() => changeSort("age")} className="inline-flex items-center gap-2">
                  {t("children.table.child_age")}
                  {sortIcon("age")}
                </button>
              </th>
              <th aria-sort={ariaSort("gender")} className="px-4 py-3 text-left align-middle font-medium">
                <button type="button" onClick={() => changeSort("gender")} className="inline-flex items-center gap-2">
                  {t("children.table.gender", { defaultValue: "Gender" })}
                  {sortIcon("gender")}
                </button>
              </th>
              <th className="px-4 py-3 text-center align-middle font-medium">{t("classrooms.details.current")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedChildren.map((child) => {
              const clickable = canViewChild;
              const hasDob = !!child.dateBirth;
              return (
                <tr
                  key={child.id}
                  className={`group transition-colors duration-200 even:bg-gray-50 dark:even:bg-gray-900/40 ${
                    clickable
                      ? "cursor-pointer hover:bg-gradient-to-r hover:from-indigo-50/80 hover:via-white hover:to-white hover:shadow-[0_15px_35px_rgba(79,70,229,0.15)] hover:ring-1 hover:ring-indigo-200/70 dark:hover:from-gray-800/70 dark:hover:via-gray-900/80 dark:hover:to-gray-900/60 dark:hover:shadow-[0_15px_35px_rgba(15,23,42,0.45)] dark:hover:ring-1 dark:hover:ring-indigo-500/40"
                      : ""
                  }`}
                  style={{ transitionProperty: "background, box-shadow" }}
                  onClick={() => clickable && onViewChild(child.id)}
                >
                  {canManage && (
                    <td className="w-12 px-4 py-3 text-center align-middle">
                      <input
                        type="checkbox"
                        checked={selectedChildIds.has(child.id)}
                        onChange={() => toggleChildSelection(child.id)}
                        onClick={(event) => event.stopPropagation()}
                        disabled={Boolean(child.current) || selectionLoading}
                        aria-label={t("classrooms.details.select_child", {
                          name: childName(child),
                          defaultValue: "Select {{name}}",
                        })}
                        className="h-4 w-4 rounded border-indigo-200 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 align-middle text-gray-900 dark:text-gray-100">
                    <span className="font-semibold">
                      {child.firstName} {child.lastName}
                    </span>
                    <span className="ml-3 inline-flex items-center gap-2">
                      <CommittedValidate committed={child.committed} />
                      <WaitingValidate waiting={child.waiting} />
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-600 dark:text-gray-300">
                    {hasDob ? calculateAge(child.dateBirth) : t("common.not_available")}
                  </td>
                  <td className="px-4 py-3 align-middle text-gray-600 dark:text-gray-300">
                    <GenderIcon gender={child.gender} />
                  </td>
                  <td className="px-4 py-3 text-center align-middle">
                    {child.current ? (
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        <span
                          className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
                          title={t("classrooms.details.current_active", { defaultValue: "Current" })}
                        >
                          {t("classrooms.details.current_active", { defaultValue: "Current" })}
                        </span>
                        {canManage && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onUnassign(child.id);
                            }}
                            className="lumen-glow-sheen inline-flex items-center rounded-full border border-red-200/80 bg-red-50/80 px-3 py-1 text-xs font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/50 dark:bg-red-900/30 dark:text-red-200"
                          >
                            {t("classrooms.details.unassign")}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {canManage ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onMakeCurrent(child.id);
                              }}
                              className="lumen-glow-sheen inline-flex items-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
                              title={t("classrooms.details.set_current", { defaultValue: "Make current" })}
                            >
                              {t("classrooms.details.current_active", { defaultValue: "Current" })}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onUnassign(child.id);
                              }}
                              className="lumen-glow-sheen inline-flex items-center rounded-full border border-gray-200/70 bg-white/80 px-3 py-1 text-xs font-semibold text-gray-600 transition hover:text-gray-900 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-200"
                            >
                              {t("classrooms.details.unassign")}
                            </button>
                          </>
                        ) : (
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                            {t("classrooms.details.not_current", { defaultValue: "Not current" })}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        {t("classrooms.details.no_children_assigned")}
      </p>
    )}
  </section>
  );
};

export default ChildrenSection;
